import { ComponentRef, TemplateRef, Type } from "@angular/core";
import { DialogPositions } from "ng/src/app/shared/model/primeng-extensions.model";
import { Dialog } from "primeng/dialog";
import { PersistentDialogComponent } from "ng/src/app/shared/persistent-dialog/persistent-dialog.component";

export type PersistentDialogSettings = {
	closeable?: boolean;
	header?: string | TemplateRef<any>;
	footer?: TemplateRef<any>;
	escapeToClose?: boolean | (() => boolean);
	onClose?: () => void;
	breakpoints?: Record<string, string>;
	width?: string;
	fullscreen?: true,
	position?: DialogPositions;
	locationChangeOnClose?: boolean;
}

export type PersistentDialogTemplateUpdate = (TemplateRef<any> | {
	template: TemplateRef<any>;
	settings: PersistentDialogSettings;
});

export type PersistentDialogComponentUpdate = ( Type<any> | {
	component: Type<any>,
	settings: PersistentDialogSettings
});

export class PersistentDialogController<T = any> {
	private dialogComponent?: PersistentDialogComponent;
	private _destroyed = false;
	visible = true;
	
	/**
	 * Whether the dialog will close when a $routeChangeStart or $routeUpdate event takes place
	 */
	closeOnNavigation = false;
	
	/**
	 * Whether the dialog will close when the escape key is pressed
	 */
	closeOnEscape = true;
	
	/**
	 * Whether the dialog will close when PersistentDialogService.close() method is called.
	 */
	respondToServiceClose = false;
	
	get destroyed() {
		return this._destroyed;
	}
	
	/**
	 * Triggers when the dialog is first created
	 */
	public onDialogCreated?: (dialog: Dialog) => void;
	/**
	 * Method that triggers once the dialog becomes visible on screen
	 */
	onDialogVisible?: (element: HTMLDivElement, dialog: Dialog) => void;
	/**
	 * Method to allow manipulating the created component before it is inserted into the view
	 */
	onComponentCreated?: (component: ComponentRef<T>) => void;
	
	closeConfirmation?: (close: (() => void)) => void;
	
	onDialogClosed?: () => void;
	
	constructor(
		public readonly componentType: Type<T>
	) {}
	
	setOnDialogCreated(func: (dialog: Dialog) => void) {
		this.onDialogCreated = func;
		return this;
	}
	
	setOnDialogVisible(func: (element: HTMLDivElement, dialog: Dialog) => void) {
		this.onDialogVisible = func;
		return this;
	}
	
	setOnComponentCreated(func: (component: ComponentRef<T>) => void) {
		this.onComponentCreated = func;
		return this;
	}
	
	setCloseConfirmation(func: (close: () => void) => void) {
		this.closeConfirmation = func;
		return this;
	}
	
	setOnDialogClose(func: () => void) {
		this.onDialogClosed = func;
		return this;
	}
	
	setCloseOnNavigation(val: boolean) {
		this.closeOnNavigation = val;
		return this;
	}
	
	setCloseOnEscape(val: boolean) {
		this.closeOnEscape = val;
		return this;
	}
	
	setRespondToServiceClose(val: boolean) {
		this.respondToServiceClose = val;
		return this;
	}
	
	register(component: PersistentDialogComponent) {
		this.dialogComponent = component;
	}
	
	close() {
		if (this._destroyed) return;
		
		const destroy = () => {
			this._destroyed = true;
			const idx = this.dialogComponent?.controlledDialogs?.indexOf(this) ?? -1;
			if (idx >= 0) this.dialogComponent?.controlledDialogs?.splice(idx, 1);
			this.onDialogClosed?.();
		}
		
		if (typeof this.closeConfirmation === 'function') {
			this.closeConfirmation(() => destroy());
		}
		else {
			destroy();
		}
	}
}

export type PersistentDialogUpdate = PersistentDialogTemplateUpdate | PersistentDialogComponentUpdate | PersistentDialogController;